import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import type {
  AdministeredOrganisation,
  GetMyOrganisationResponse,
} from '@dreamai/types';
import api from '../api';
import PublicNoticePage from './common/PublicNoticePage';
import OrganisationSection from './settings/OrganisationSection';
import './account-deactivated-page.scss';

export type AccountDeactivatedReason =
  | 'member_deactivated'
  | 'organisation_closed';

export interface AccountDeactivatedInfo {
  reason: AccountDeactivatedReason;
  organisationName?: string;
  message?: string;
}

/** Shape of the backend's `403 account_deactivated` body, if it is one. */
export function parseAccountDeactivated(
  body: unknown,
): AccountDeactivatedInfo | null {
  if (typeof body !== 'object' || body === null) return null;
  const record = body as Record<string, unknown>;
  if (record.error !== 'account_deactivated') return null;
  const reason =
    record.reason === 'organisation_closed'
      ? 'organisation_closed'
      : 'member_deactivated';
  return {
    reason,
    organisationName:
      typeof record.organisationName === 'string'
        ? record.organisationName
        : undefined,
    message: typeof record.message === 'string' ? record.message : undefined,
  };
}

/**
 * Organisations the deactivated login still administers. The backend lets a
 * deactivated seat through to `GET /organisations/me` (and to the console of
 * any organisation they actively administer) so an admin is never locked out
 * of their own organisation by their own seat. `[]` while loading or when
 * the request fails: the notice then offers sign-out only.
 */
function useAdministeredOrganisations(): AdministeredOrganisation[] {
  const { getAccessTokenSilently } = useAuth0();
  const [adminOf, setAdminOf] = useState<AdministeredOrganisation[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data } = await api.get<GetMyOrganisationResponse>(
          '/organisations/me',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setAdminOf(data.enabled ? data.adminOf : []);
      } catch (err) {
        console.error(
          'Error checking administered organisations while deactivated:',
          err,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  return adminOf;
}

/**
 * Shown when the signed-in user's account has been deactivated by their
 * organisation (or the organisation was closed). The login itself worked;
 * access is denied deliberately and reversibly, so this is explicitly not an
 * authentication error.
 *
 * An admin whose own seat was deactivated can still open the organisation
 * console from here (and nothing else in the app) so they can reactivate
 * themselves or manage the organisation.
 */
const AccountDeactivatedPage: React.FC<{ info: AccountDeactivatedInfo }> = ({
  info,
}) => {
  const { logout } = useAuth0();
  const adminOf = useAdministeredOrganisations();
  const [consoleOpen, setConsoleOpen] = useState(false);
  const orgName = info.organisationName || 'your organisation';
  const title =
    info.reason === 'organisation_closed'
      ? `${orgName} has been closed`
      : 'Your account has been deactivated';
  const body =
    info.message ||
    (info.reason === 'organisation_closed'
      ? `${orgName} closed its Dream organisation, so your account is paused. Your data is kept, but your secretary will not respond until an admin reactivates you.`
      : `An admin of ${orgName} has deactivated your account. Your data is kept, but your secretary will not respond until an admin reactivates you.`);

  const signOut = () =>
    logout({ logoutParams: { returnTo: window.location.origin } });

  if (consoleOpen && adminOf.length > 0) {
    return (
      <div
        className='account-deactivated-page account-deactivated-console'
        data-testid='account-deactivated-console'
      >
        <div className='account-deactivated-banner' role='status'>
          <span>
            <strong>{title}.</strong> You can still manage the organisations you
            administer; the rest of Dream stays paused.
          </span>
          <button
            type='button'
            className='public-notice__link'
            onClick={signOut}
          >
            Sign out
          </button>
        </div>
        <OrganisationSection initialOrganisationId={adminOf[0].id} />
      </div>
    );
  }

  return (
    <PublicNoticePage
      className='account-deactivated-page'
      testId='account-deactivated-page'
      title={title}
      body={
        <>
          {body}
          <span className='account-deactivated-hint'>
            {adminOf.length > 0
              ? 'You still administer an organisation, so its console stays open to you.'
              : `Think this is a mistake? Contact an admin of ${orgName}.`}
          </span>
        </>
      }
      actions={
        <>
          {adminOf.length > 0 && (
            <button
              type='button'
              className='public-notice__button'
              data-testid='account-deactivated-open-console'
              onClick={() => setConsoleOpen(true)}
            >
              Manage {adminOf.length === 1 ? adminOf[0].name : 'organisations'}
            </button>
          )}
          <button
            type='button'
            className={
              adminOf.length > 0
                ? 'public-notice__link'
                : 'public-notice__button'
            }
            onClick={signOut}
          >
            Sign out
          </button>
        </>
      }
    />
  );
};

export default AccountDeactivatedPage;
